import {
  Button,
  ICONS,
  PageLayout,
  Typography,
  PLATFORM_THEME_COLORS,
  TYPOGRAPHY_SIZE,
  Form,
  ButtonsWrapper,
  Separator,
  showNotification,
  NOTIFICATION_TYPE,
} from '@thinknet-gmbh/platform-common-ui';
import omit from 'lodash/omit';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { ROUTES } from 'src/components/app/app-routes.constants';
import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { useUpsertProject } from 'src/hooks/projects/upsert-project.hook';
import { CreateProjectInfo } from 'src/pages/projects/components/create-project-info';
import { CreateTask } from 'src/pages/projects/components/create-task';
import { TaskTemplatesModal } from 'src/pages/projects/components/task-templates-modal';

import { HeaderContainer } from './create-project.styles';

const CreateProject = () => {
  const [form] = Form.useForm();
  const { t } = useTranslation(['common', 'projects']);
  const [selectedGantts, setSelectedGantts] = useState<string[]>([]);
  const [activeModal, setActiveModal] = useState<'taskTemplates' | null>(null);
  const [selectedTemplatesIds, setSelectedTemplatesIds] = useState<string[]>([]);

  const navigate = useNavigate();
  const { id } = useParams();

  const currentCompanyId = useGetCurrentCompanyId();

  const { upsertProject, loading } = useUpsertProject({
    onSuccess: () => {
      navigate(ROUTES.project);
      showNotification({
        type: NOTIFICATION_TYPE.success,
        message: t('projects:notifications.projectWasAdded.title'),
      });
    },
  });

  const onFinish = (values: any) => {
    const projectNumberErrors = form.getFieldError('projectNumber');

    if (!projectNumberErrors.length) {
      const payload = {
        ...omit(values, 'projectPlannedHours'),
        tasks:
          values.tasks?.map((t: any) => ({
            ...t,
            files: t.files?.fileList.map((f: any) => f.originFileObj) || [],
          })) || [],
        companyId: currentCompanyId,
      };

      upsertProject({ variables: { payload } });
    }
  };

  useEffect(() => {
    if (id) {
      form.setFieldsValue({ customerId: id });
    }
  }, [form, id]);

  const onSubmitTaskTemplates = (values: any) => {
    setSelectedTemplatesIds([]);
    setSelectedTemplatesIds(values.taskTemplatesIds);
    setActiveModal(null);
  };

  return (
    <PageLayout pageTitle={t('projects:buttons.addNewProject')}>
      {activeModal === 'taskTemplates' && (
        <TaskTemplatesModal onClose={() => setActiveModal(null)} onFinish={onSubmitTaskTemplates} />
      )}
      <HeaderContainer>
        <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
          {t('projects:buttons.addNewProject')}
        </Typography>
        <ButtonsWrapper>
          <Button
            type="primary"
            ghost
            icon={ICONS.folderPlus}
            iconColor={PLATFORM_THEME_COLORS.primary.blue}
            onClick={() => setActiveModal('taskTemplates')}
          >
            {t('projects:buttons.useTaskTemplates')}
          </Button>
        </ButtonsWrapper>
      </HeaderContainer>
      <Form layout="vertical" form={form} onFinish={onFinish}>
        <CreateProjectInfo
          form={form}
          onSelectGantts={(values: string[]) => setSelectedGantts(values)}
        />
        <Form.List name="tasks">
          {(fields, { add, remove }) => (
            <CreateTask
              selectedGantts={selectedGantts}
              taskTemplatesIds={selectedTemplatesIds}
              addTask={add}
              removeTask={remove}
              fields={fields}
              form={form}
            />
          )}
        </Form.List>
        <Separator />
        <ButtonsWrapper marginTop={16}>
          <Button htmlType="submit" type="primary" loading={loading}>
            {t('common:buttons.save')}
          </Button>
          <Button
            onClick={() => {
              form.resetFields();
              navigate(ROUTES.project);
            }}
          >
            {t('common:buttons.cancel')}
          </Button>
        </ButtonsWrapper>
      </Form>
    </PageLayout>
  );
};

export default CreateProject;
